import * as React from 'react'

import { Alert, Button, Text, TogglerCheckbox } from '@factorialco/deprecated-design-system'
import { Field, Form } from 'factorial-form'
import moment from 'moment'

import { observer } from 'mobx-react'

import FormRow from 'modules/core/components/FormRow'
import DayPicker from 'modules/core/components/Inputs/DayPicker'
import InformationTooltip from 'modules/core/components/Inputs/InformationTooltip'
import Select from 'modules/core/components/Inputs/Select'
import LabeledInput from 'modules/core/components/LabeledInput'
import Modal from 'modules/core/components/Modal'
import type { ClosePortalFn } from 'modules/core/components/Portal/useCloseProxy'
import asyncAction from 'modules/core/lib/asyncAction'
import { dateToString, stringToDate } from 'modules/core/lib/dates'
import i18n from 'modules/core/lib/i18n'

import type SurveyForm from 'modules/surveys/store/models/Form'

interface Props {
  form: SurveyForm
  onClose: () => void
  edit?: boolean
}

function SurveyStartModal({ form: surveyForm, onClose, edit }: Props) {
  const [running, setRunning] = React.useState(false)
  const [closeReviewNow, setcloseReviewNow] = React.useState(false)

  const submit = async (closePortal: ClosePortalFn) => {
    setRunning(true)

    const { success, value } = await asyncAction(() => {
      const endOn = stringToDate(form.get('end_on').value, 'ui')

      if (!edit) return surveyForm.rpc('start_form', form.data())

      return surveyForm.rpc('update_form_end_date', {
        end_on:
          form.get('end_on').value === '' && !closeReviewNow
            ? null
            : dateToString(closeReviewNow ? moment() : endOn, 'backend'),
      })
    })
    setRunning(false)

    if (success) {
      surveyForm.set(value)
    }
    closePortal()
  }

  const onChangeToggle = () => {
    setcloseReviewNow(!closeReviewNow)
  }

  const editEndOn = edit ? surveyForm.attributes.end_on : null

  const form = React.useRef(
    new Form(
      {
        start_on: edit ? surveyForm.attributes.start_on : dateToString(moment(), 'backend'),
        schedule: edit ? surveyForm.periodicity : null,
        end_on: surveyForm.isFinished ? null : editEndOn,
      },
      {
        start_on: 'date',
        schedule: 'string',
        end_on: 'date',
      }
    )
  ).current

  const options = [
    {
      value: 'daily',
      label: i18n.t('surveys.form.periodicity.daily'),
    },
    {
      value: 'weekly',
      label: i18n.t('surveys.form.periodicity.weekly'),
    },
    {
      value: 'bi_monthly',
      label: i18n.t('surveys.form.periodicity.bi_monthly'),
    },
    {
      value: 'monthly',
      label: i18n.t('surveys.form.periodicity.monthly'),
    },
    {
      value: 'quarterly',
      label: i18n.t('surveys.form.periodicity.quarterly'),
    },
    {
      value: 'bi_annual',
      label: i18n.t('surveys.form.periodicity.bi_annual'),
    },
    {
      value: 'annual',
      label: i18n.t('surveys.form.periodicity.yearly'),
    },
  ]

  const startTitle = i18n.t('surveys.form.start.title.survey')

  const startDescription = i18n.t('surveys.form.start.description.survey')

  const oneTimeLabel = i18n.t('surveys.form.periodicity.one_time.survey')

  const editDescription = i18n.t('surveys.form.edit_form.description.survey')

  const editTitle = i18n.t('surveys.form.edit_form.title.name')

  const reactivateDescription = i18n.t(
    'surveys.review.configuration.stats.reactivate_survey_description'
  )

  const reactivateTitle = i18n.t('surveys.review.configuration.stats.reactivate_survey')

  const activeDescription = edit ? editDescription : startDescription
  const activeTitle = edit ? editTitle : startTitle
  const title = surveyForm.isFinished ? reactivateTitle : activeTitle

  const description = surveyForm.isFinished ? reactivateDescription : activeDescription

  const activeFooter = edit
    ? i18n.t('surveys.form.edit_form.submit')
    : i18n.t('surveys.form.start.form.submit')

  const close = i18n.t('surveys.form.start.form.labels.close_survey')

  const warning = i18n.t('surveys.form.start.form.labels.warning_survey')

  const reactiveFooter = i18n.t('surveys.form.edit_form.reactivate')
  const footer = surveyForm.isFinished ? reactiveFooter : activeFooter

  const today = React.useRef(new Field(dateToString(moment(), 'ui'), 'string')).current

  const tomorrow = moment().startOf('day').add(1, 'days')

  return (
    <Modal
      title={title}
      description={description}
      illustration="server-edit"
      onClose={() => {
        onClose()
      }}
      onSubmit={submit}
      footer={
        <Button disabled={running} submit>
          {footer}
        </Button>
      }
    >
      {!surveyForm.isFinished && (
        <>
          <FormRow>
            <Select
              readonly={!!edit}
              none={edit ? surveyForm.periodicity : oneTimeLabel}
              label={i18n.t('surveys.form.start.form.labels.schedule')}
              field={form.get('schedule')}
              options={options}
            />
          </FormRow>
          <FormRow>
            <DayPicker
              readonly={!!edit}
              mandatory
              field={form.get('start_on')}
              label={i18n.t('surveys.form.start.form.labels.start_on')}
              minDate={moment().startOf('day')}
            />
          </FormRow>
        </>
      )}
      <FormRow>
        <LabeledInput
          isLegend
          label={i18n.t('surveys.form.start.form.labels.end_on')}
          secondaryLabel={
            <InformationTooltip
              information={i18n.t('surveys.form.start.form.labels.information')}
              icon="question"
            />
          }
        >
          <DayPicker
            readonly={!!closeReviewNow}
            field={closeReviewNow ? today : form.get('end_on')}
            minDate={surveyForm.isFinished ? tomorrow : moment().startOf('day')}
            withNullLabel={i18n.t('surveys.form.start.form.labels.no_due_date')}
            withNull
            withNullInitialDate={surveyForm.isFinished ? tomorrow : moment().startOf('day')}
          />
        </LabeledInput>
      </FormRow>
      {edit && !surveyForm.isFinished && (
        <FormRow>
          <TogglerCheckbox value={closeReviewNow} label={close} onChange={onChangeToggle} />
        </FormRow>
      )}
      {closeReviewNow && (
        <FormRow>
          <Alert color="warning">
            <Text.Normal>{warning}</Text.Normal>
          </Alert>
        </FormRow>
      )}
    </Modal>
  )
}

export default observer(SurveyStartModal)
